import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CONTACT_INFO } from "@/lib/index";
import { COMPANY_INFO } from "@/data/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const formSchema = z.object({
  name: z.string().min(2, { message: "Le nom doit contenir au moins 2 caractères." }),
  email: z.string().email({ message: "Veuillez entrer une adresse email valide." }),
  subject: z.string().min(5, { message: "Le sujet doit contenir au moins 5 caractères." }),
  message: z.string().min(10, { message: "Le message doit contenir au moins 10 caractères." }),
});

const Contact = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
    toast.success("Message envoyé avec succès !", {
      description: "Nous vous répondrons dans les plus brefs délais.",
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
    });
    form.reset();
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--primary)_0%,transparent_40%)] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              Contactez <span className="text-primary">YKE Multi_Services</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Une question sur nos vins, nos résidences ou nos parcelles ? 
              Notre équipe d'experts est à votre entière disposition pour vous accompagner.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-3xl font-heading font-semibold">Nos Coordonnées</h2>
                <p className="text-muted-foreground">
                  Retrouvez-nous à notre siège social à Port-Bouët ou contactez-nous directement via les canaux ci-dessous.
                </p>
              </div>

              <div className="grid gap-6">
                <Card className="border-none bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Adresse</h3>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.address}</p>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.city}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Téléphones</h3>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.phone1}</p>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.phone2}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email Professionnel</h3>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.email}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Horaires d'Ouverture</h3>
                      <p className="text-sm text-muted-foreground">{CONTACT_INFO.openingHours}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <Card className="border-primary/20 bg-card overflow-hidden shadow-2xl">
                <div className="bg-primary px-8 py-6">
                  <h2 className="text-2xl font-heading font-bold text-primary-foreground">Envoyez-nous un message</h2>
                  <p className="text-primary-foreground/80 text-sm">Nous traitons vos demandes sous 24h ouvrées.</p>
                </div>
                <CardContent className="p-8">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nom Complet</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex: Jean Kouassi" {...field} className="bg-muted/50" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Adresse Email</FormLabel>
                              <FormControl>
                                <Input placeholder="votre@email.com" {...field} className="bg-muted/50" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Objet</FormLabel>
                            <FormControl>
                              <Input placeholder="Le motif de votre message" {...field} className="bg-muted/50" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Comment pouvons-nous vous aider ?"
                                className="min-h-[150px] bg-muted/50"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full py-6 text-lg group">
                        Envoyer le message
                        <Send className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="h-[400px] w-full grayscale contrast-125 opacity-80 hover:grayscale-0 transition-all duration-700">
        <iframe
          title="Localisation SIPIM Port-Bouët"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15893.4244837517!2d-3.9482!3d5.2638!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfc1ec96e70390f7%3A0x63351d36894c2e42!2sPort-Bou%C3%ABt%2C%20Abidjan%2C%20C%C3%B4te%20d&#39;Ivoire!5e0!3m2!1sfr!2sfr!4v1708290000000!5m2!1sfr!2sfr"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>

      <footer className="py-12 bg-card border-t">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">
            © {COMPANY_INFO.year} {COMPANY_INFO.companyName} - {COMPANY_INFO.slogan}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;